<template>
    <div class="list-panel">
        <div class="list-header">
            <div class="list-header-copy">
                <span class="list-title">상품 목록</span>
            </div>

            <CustomDropdown class="list-header-dropdown" :items="addMenuItems" align="right">
                <template #trigger>
                    <button
                        type="button"
                        class="panel-header-button panel-header-button--dropdown-trigger"
                        title="작업"
                    >
                        <i class="fas fa-ellipsis-v"></i>
                    </button>
                </template>
            </CustomDropdown>
        </div>

        <div class="list-search">
            <i class="fas fa-search search-ico"></i>
            <input v-model.trim="keyword" class="search-inp" placeholder="상품명 또는 코드 검색" type="text" />
            <button v-if="keyword" type="button" class="search-clear" title="검색어 지우기" @click="keyword = ''">
                <i class="fas fa-times"></i>
            </button>
        </div>

        <div class="list-filter">
            <button
                v-for="filter in filters"
                :key="filter.value"
                type="button"
                class="filter-chip"
                :class="{ on: useFilter === filter.value }"
                @click="useFilter = filter.value"
            >
                {{ filter.label }}
            </button>
        </div>

        <div class="list-body">
            <div
                v-for="product in filteredList"
                :key="product.product_id"
                class="list-item"
                :class="{ selected: selectedId === product.product_id, 'list-item--inactive': product.use_yn !== 'Y' }"
                @click="$emit('select', product)"
            >
                <div class="item-top">
                    <div class="item-main">
                        <span class="item-name">{{ product.product_name }}</span>
                        <span class="item-code">{{ product.product_code }}</span>
                    </div>
                </div>

                <div class="item-meta">
                    <span class="badge-unit">{{ product.product_unit }}</span>
                    <span class="badge-yn" :class="product.use_yn === 'Y' ? 'yn-on' : 'yn-off'">
                        {{ product.use_yn === "Y" ? "사용" : "미사용" }}
                    </span>
                    <span class="badge-link" :class="isPointLinked(product) ? 'badge-link--on' : 'badge-link--off'">
                        <i class="fas fa-link"></i>
                        <template v-if="isPointLinked(product)">
                            <span v-if="linkedPointName(product)" class="badge-link-point">
                                {{ linkedPointName(product) }}
                            </span>
                            <span v-else class="badge-link-point badge-link-point--empty">관제점명 미지정</span>
                        </template>
                        <template v-else>미연결</template>
                    </span>
                </div>
            </div>

            <div v-if="!filteredList.length" class="list-empty">
                <i class="fas fa-inbox"></i>
                <strong>조건에 맞는 상품이 없습니다</strong>
                <span>검색어 또는 상태 필터를 다시 확인해 주세요.</span>
            </div>
        </div>

        <div class="list-footer">
            <span>{{ filteredList.length }} / {{ products.length }}개 표시</span>
            <span v-if="selectedProduct" class="list-footer-focus">{{ selectedProduct.product_name }}</span>
        </div>
    </div>
</template>

<script>
import CustomDropdown from "@components/dropdown/CustomDropdown.vue";

export default {
    name: "ProductListPanel",
    components: { CustomDropdown },
    props: {
        products: { type: Array, default: () => [] },
        selectedId: { type: Number, default: null },
    },
    data() {
        return {
            keyword: "",
            useFilter: "",
            filters: [
                { value: "", label: "전체" },
                { value: "Y", label: "사용" },
                { value: "N", label: "미사용" },
            ],
        };
    },
    computed: {
        addMenuItems() {
            const self = this;
            return [
                {
                    label: "상품 추가",
                    icon: "fa-plus",
                    onClick() {
                        self.$emit("add");
                    },
                },
            ];
        },
        filteredList() {
            let list = this.products;
            if (this.useFilter) list = list.filter((product) => product.use_yn === this.useFilter);
            if (this.keyword) {
                const keyword = this.keyword.toLowerCase();
                list = list.filter(
                    (product) =>
                        product.product_name.toLowerCase().includes(keyword) ||
                        product.product_code.toLowerCase().includes(keyword),
                );
            }
            return list;
        },
        selectedProduct() {
            return this.products.find((product) => product.product_id === this.selectedId) || null;
        },
    },
    methods: {
        isPointLinked(product) {
            if (!product) return false;
            return Boolean(
                product.pt_addr ||
                product.pt_name ||
                (product.default_prod_pt_idx != null && product.default_prod_pt_idx !== ""),
            );
        },
        linkedPointName(product) {
            if (!product) return "";
            const nameCandidates = [
                product.pt_name,
                product.default_prod_pt_name,
                product.defaultProdPtName,
                product.defaultProdPointName,
                product.defaultProdPointNm,
                product.defaultProdPtNm,
                product.prodPtNm,
                product.ptName,
            ];
            for (let i = 0; i < nameCandidates.length; i += 1) {
                const raw = nameCandidates[i];
                if (raw == null) continue;
                const trimmed = String(raw).trim();
                if (trimmed) return trimmed;
            }
            return "";
        },
    },
};
</script>

<style scoped>
.list-panel {
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 0.98));
}

/* 상품 카드 헤더(.pic-header)와 동일: 패딩·타이포·세로 정렬만 맞춤 (고정 height 없음) */
.list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px 10px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.92);
    background: linear-gradient(180deg, rgba(239, 246, 255, 0.92), rgba(255, 255, 255, 0.88));
    backdrop-filter: blur(10px);
    flex-shrink: 0;
    border-radius: 12px 12px 0 0;
}

.list-header-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.list-title {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--puc-navy, #1b263b);
    letter-spacing: -0.01em;
}

.list-header-dropdown {
    flex-shrink: 0;
}

.panel-header-button {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    min-height: 32px;
    padding: 0 11px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--puc-slate-500, #64748b);
    cursor: pointer;
    transition:
        color 0.18s ease,
        background 0.18s ease,
        border-color 0.18s ease;
}

.panel-header-button--dropdown-trigger:hover {
    color: var(--puc-navy, #1b263b);
    background: rgba(241, 245, 249, 0.9);
    border-color: rgba(226, 232, 240, 0.95);
}

.panel-header-button--dropdown-trigger:focus-visible {
    outline: 2px solid rgba(0, 123, 255, 0.26);
    outline-offset: 2px;
}

.list-search {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 10px 0;
    padding: 0 10px;
    min-height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(226, 232, 240, 0.94);
    background: rgba(255, 255, 255, 0.92);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
}

.search-ico {
    font-size: 12px;
    color: var(--puc-slate-400, #94a3b8);
    flex-shrink: 0;
}

.search-inp {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    font-size: 12px;
    color: var(--puc-navy, #1b263b);
    background: transparent;
}

.search-inp::placeholder {
    color: var(--puc-slate-300, #cbd5e1);
}

.search-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 50%;
    background: rgba(148, 163, 184, 0.16);
    color: var(--puc-slate-500, #64748b);
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.search-clear:hover {
    background: rgba(244, 63, 94, 0.12);
    color: var(--puc-red, #f43f5e);
}

.list-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 8px 10px 10px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.92);
    flex-shrink: 0;
}

.filter-chip {
    box-sizing: border-box;
    min-height: 26px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: rgba(255, 255, 255, 0.85);
    color: var(--puc-slate-500, #64748b);
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        color 0.18s ease,
        background 0.18s ease;
}

.filter-chip:hover {
    border-color: rgba(148, 163, 184, 0.65);
    color: var(--puc-slate-900, #334155);
}

.filter-chip.on {
    border-color: rgba(37, 99, 235, 0.28);
    background: rgba(0, 123, 255, 0.09);
    color: var(--puc-blue-strong, #2563eb);
}

.list-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
}

.list-item {
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding: 10px;
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    transition:
        transform 0.18s ease,
        box-shadow 0.18s ease,
        border-color 0.18s ease,
        background 0.18s ease;
}

.list-item:hover {
    transform: translateY(-1px);
    border-color: rgba(148, 163, 184, 0.72);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.05);
}

.list-item.selected {
    border-color: rgba(37, 99, 235, 0.28);
    background: linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(255, 255, 255, 0.98));
    box-shadow:
        inset 0 0 0 1px rgba(0, 123, 255, 0.08),
        0 14px 30px rgba(37, 99, 235, 0.08);
}

.list-item--inactive {
    opacity: 0.9;
}

.item-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.item-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.item-name {
    font-size: 12px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
    line-height: 1.35;
}

.item-code {
    font-size: 10px;
    color: var(--puc-slate-400, #94a3b8);
}

.item-selected {
    display: inline-flex;
    align-items: center;
    min-height: 20px;
    padding: 3px 7px;
    border-radius: 999px;
    background: rgba(0, 123, 255, 0.1);
    color: var(--puc-blue-strong, #2563eb);
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
}

.item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.badge-unit,
.badge-yn,
.badge-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 20px;
    padding: 3px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.badge-unit {
    color: var(--puc-slate-700, #475569);
    background: rgba(241, 245, 249, 0.95);
}

.yn-on {
    color: var(--puc-green, #059669);
    background: rgba(5, 150, 105, 0.11);
}

.yn-off {
    color: var(--puc-slate-500, #64748b);
    background: rgba(148, 163, 184, 0.12);
}

.badge-link--on {
    color: var(--puc-blue-strong, #2563eb);
    background: rgba(0, 123, 255, 0.09);
}

.badge-link--off {
    color: var(--puc-slate-500, #64748b);
    background: rgba(241, 245, 249, 0.95);
}

.badge-link-point {
    display: inline-block;
    max-width: 128px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.badge-link-point--empty {
    color: var(--puc-slate-400, #94a3b8);
}

.list-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 24px 12px;
    border: 1px dashed rgba(203, 213, 225, 0.95);
    border-radius: 12px;
    background: rgba(248, 250, 252, 0.72);
    text-align: center;
}

.list-empty i {
    font-size: 22px;
    color: var(--puc-slate-300, #cbd5e1);
}

.list-empty strong {
    font-size: 12px;
    color: var(--puc-slate-500, #64748b);
}

.list-empty span {
    font-size: 10px;
    color: var(--puc-slate-400, #94a3b8);
    line-height: 1.5;
}

.list-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-top: 1px solid rgba(241, 245, 249, 0.94);
    background: rgba(248, 250, 252, 0.82);
    color: var(--puc-slate-500, #64748b);
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.list-footer-focus {
    max-width: 60%;
    color: var(--puc-navy, #1b263b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
